import { apiSlice } from "../../app/api/apiSlice";
import { COMPANY_ADMIN } from "../../utils/common-constants";

const normalizedValue = (value) => String(value ?? "").trim().toLowerCase();

const getOrganizationKey = (provider, currentOrganizationId) =>
	provider.organization_name ??
	provider.organizationName ??
	provider.organization?.name ??
	provider.organization_id ??
	provider.organizationId ??
	provider.organization?.id ??
	currentOrganizationId;

// The API normally returns records for one organization, but retain the
// organization in the key so a vendor configured by another organization is
// never incorrectly removed if that response scope changes.
export const removeDuplicateCreditVendors = (providers, currentOrganizationId) => {
	const seen = new Set();

	return (Array.isArray(providers) ? providers : []).filter((provider) => {
		const creditVendor = normalizedValue(
			provider.credit_vendor ?? provider.creditVendor
		);
		if (!creditVendor) return true;

		const key = `${normalizedValue(
			getOrganizationKey(provider, currentOrganizationId)
		)}:${creditVendor}`;
		if (seen.has(key)) return false;

		seen.add(key);
		return true;
	});
};

export const filterVisibleCreditVendors = (
	providers,
	{ accessLevel, userEmail } = {}
) => {
	const records = Array.isArray(providers) ? providers : [];

	if (accessLevel === COMPANY_ADMIN) return records;

	const currentUserEmail = normalizedValue(userEmail);
	return records.filter(
		(provider) =>
			provider.added_by_admin !== true &&
			normalizedValue(provider.user_email) === currentUserEmail
	);
};

// The email and access level are cache/response-filtering inputs only. They are
// deliberately never included in the HTTP request; the API receives only the
// organization scope and must enforce the same visibility rule server-side.
export const getCreditVendorDetailsQueryArgs = (organizationId, userDetails) => {
	if (!organizationId || !userDetails) return undefined;

	return {
		organizationId,
		userEmail: userDetails.email,
		accessLevel: userDetails.access_level,
	};
};

export const creditVendorsApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		creditVendors: builder.mutation({
			query: (credentials) => ({
				url: `/api/v1/loan/credit_partners/`,
				method: "GET",
			}),
		}),
		creditVendorPublicKey: builder.mutation({
			query: () => ({
				url: "/api/v1/credit-vendor/public-key/",
				method: "GET",
				headers: { Accept: "application/json" },
				cache: "no-store",
			}),
		}),
		getCreditVendorDetails: builder.query({
			query: ({ organizationId }) => ({
				url: "/api/v1/credit_vendor_details/",
				method: "GET",
				headers: {
					"tpo-org-id": String(organizationId),
				},
			}),
			// Retain the complete response in the cache. Non-admin display surfaces
			// filter admin-created records locally, while the loan form needs those
			// hidden records to prepopulate a masked Subscriber ID.
			transformResponse: (
				response,
				_meta,
				_queryArgs
			) => {
				return Array.isArray(response) ? response : [];
			},
		}),
		getCreditVendorDetailsForDuplicateCheck: builder.mutation({
			query: (organizationId) => ({
				url: "/api/v1/credit_vendor_details/",
				method: "GET",
				// The API obtains the caller's identity and access level from the
				// authenticated request, never from a client-supplied email header.
				headers: { "tpo-org-id": String(organizationId) },
			}),
		}),
		updateCreditVendorDetails: builder.mutation({
			query: ({ id, ...values }) => ({
				url: `/api/v1/credit_vendor_details/${id}/`,
				method: "PUT",
				body: values,
			}),
		}),
		deleteCreditVendorDetails: builder.mutation({
			query: ({ id, ...values }) => ({
				url: `/api/v1/credit_vendor_details/${id}/`,
				method: "DELETE",
			}),
		}),
		saveCreditVendorDetails: builder.mutation({
			query: (values) => ({
				url: "/api/v1/credit_vendor_details/",
				method: "POST",
				body: values,
			}),
		}),
	}),
});

export const {
	useCreditVendorsMutation,
	useCreditVendorPublicKeyMutation,
	useGetCreditVendorDetailsQuery,
	useGetCreditVendorDetailsForDuplicateCheckMutation,
	useSaveCreditVendorDetailsMutation,
	useUpdateCreditVendorDetailsMutation,
	useDeleteCreditVendorDetailsMutation,
} = creditVendorsApiSlice;
